<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import { useClipboard } from "@vueuse/core";
import {
    ArrowUpRight,
    Check,
    Clipboard,
    Globe2,
    LayoutPanelTop,
    Settings2,
    Users,
} from "lucide-vue-next";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import route from "ziggy-js";
import AppLayout from "@/Layouts/AppLayout.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
    BookingPage,
    BookingPageGroups,
    BookingPageState,
} from "@/types/BookingPage";

const props = defineProps<{ bookingPages: BookingPageGroups }>();
const { copy, copied } = useClipboard();
const { t } = useI18n();

const sections = computed(() => [
    {
        key: "personal" as const,
        title: t("my_page.overview.personal"),
        description: t("my_page.overview.personal_description"),
        pages: props.bookingPages.personal,
    },
    {
        key: "owned" as const,
        title: t("my_page.overview.owned"),
        description: t("my_page.overview.owned_description"),
        pages: props.bookingPages.owned,
    },
    {
        key: "joined" as const,
        title: t("my_page.overview.joined"),
        description: t("my_page.overview.joined_description"),
        pages: props.bookingPages.joined,
    },
]);

function stateLabel(state: BookingPageState): string {
    return {
        active: t("my_page.state.active"),
        needs_setup: t("my_page.state.needs_setup"),
        no_active_event_types: t("my_page.state.no_active_event_types"),
    }[state];
}

function stateClasses(state: BookingPageState): string {
    return {
        active: "bg-green-50 text-green-700",
        needs_setup: "bg-amber-50 text-amber-700",
        no_active_event_types: "bg-orange-50 text-orange-700",
    }[state];
}

function stateDotClasses(state: BookingPageState): string {
    return {
        active: "bg-green-500",
        needs_setup: "bg-amber-500",
        no_active_event_types: "bg-orange-500",
    }[state];
}

function displayUrl(page: BookingPage): string {
    return page.public_url.replace(/^https?:\/\//, "");
}

function pageHeading(page: BookingPage): string {
    if (page.team.personal_team) {
        return page.title || page.team.name;
    }

    return page.team.name;
}

function roleLabel(page: BookingPage): string {
    if (page.team.personal_team) return t("my_page.roles.personal");

    return {
        owner: t("my_page.roles.owner"),
        admin: t("my_page.roles.admin"),
        editor: t("my_page.roles.editor"),
        viewer: t("my_page.roles.viewer"),
    }[page.role];
}
</script>

<template>
    <AppLayout>
        <template #icon>
            <LayoutPanelTop :size="24" class="text-iron-500" />
        </template>
        <template #title>{{ $t("my_page.title") }}</template>
        <template #description>{{ $t("my_page.description") }}</template>

        <div class="px-4 py-6 sm:px-6 sm:py-8">
            <div class="max-w-6xl gap-10 mx-auto flex flex-col">
                <section
                    v-for="section in sections"
                    :key="section.key"
                    v-show="section.pages.length > 0"
                >
                    <header class="mb-4">
                        <p
                            class="text-xs font-semibold text-iron-400 tracking-[0.16em] uppercase"
                        >
                            {{ section.title }}
                        </p>
                        <p class="mt-1 text-sm text-iron-500">
                            {{ section.description }}
                        </p>
                    </header>

                    <div
                        class="rounded-xl border-iron-200 bg-white overflow-hidden border"
                    >
                        <article
                            v-for="(page, index) in section.pages"
                            :key="page.id"
                            class="gap-4 p-5 sm:flex-row sm:items-center hover:bg-iron-50/50 flex flex-col transition-colors"
                            :class="{ 'border-iron-100 border-t': index > 0 }"
                        >
                            <div class="min-w-0 gap-4 flex flex-1 items-center">
                                <div
                                    class="h-11 w-11 rounded-lg bg-iron-100 text-iron-500 flex shrink-0 items-center justify-center overflow-hidden"
                                >
                                    <img
                                        v-if="page.team.logo_url || page.avatar"
                                        :src="
                                            page.team.logo_url ??
                                            page.avatar ??
                                            ''
                                        "
                                        alt=""
                                        class="h-full w-full object-cover"
                                    />
                                    <Users v-else :size="19" />
                                </div>
                                <div class="min-w-0">
                                    <div
                                        class="gap-2 flex flex-wrap items-center"
                                    >
                                        <h2
                                            class="font-semibold text-iron-900 truncate"
                                        >
                                            {{ pageHeading(page) }}
                                        </h2>
                                        <Badge
                                            variant="outline"
                                            class="font-medium text-iron-500"
                                        >
                                            {{ roleLabel(page) }}
                                        </Badge>
                                    </div>
                                    <p
                                        v-if="
                                            page.title &&
                                            page.title !== pageHeading(page)
                                        "
                                        class="mt-0.5 text-sm text-iron-500 truncate"
                                    >
                                        {{ page.title }}
                                    </p>
                                    <div
                                        class="mt-1.5 gap-x-3 gap-y-1 flex flex-wrap items-center"
                                    >
                                        <span
                                            class="gap-1.5 px-2 py-0.5 text-xs font-medium inline-flex items-center rounded-full"
                                            :class="stateClasses(page.state)"
                                        >
                                            <span
                                                class="h-1.5 w-1.5 rounded-full"
                                                :class="
                                                    stateDotClasses(page.state)
                                                "
                                            />
                                            {{ stateLabel(page.state) }}
                                        </span>
                                        <span class="text-xs text-iron-500">
                                            {{
                                                $t(
                                                    "my_page.overview.counts_short",
                                                    {
                                                        active: page.active_count,
                                                        attached:
                                                            page.attached_count,
                                                    },
                                                )
                                            }}
                                        </span>
                                        <a
                                            :href="page.public_url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="text-xs text-iron-400 hover:text-iron-700 truncate"
                                        >
                                            {{ displayUrl(page) }}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div class="gap-3 flex shrink-0 items-center">
                                <div class="inline-flex">
                                    <button
                                        type="button"
                                        class="rounded-l-md bg-white p-2 text-sm font-medium ring-iron-300 hover:bg-iron-50 relative inline-flex max-h-[36px] items-center ring-1 ring-inset focus:z-10"
                                        @click="copy(page.public_url)"
                                    >
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <span
                                                        class="inline-flex items-center"
                                                    >
                                                        <Check
                                                            v-if="copied"
                                                            class="text-iron-500"
                                                            :size="20"
                                                        />
                                                        <Clipboard
                                                            v-else
                                                            class="text-iron-500"
                                                            :size="20"
                                                        />
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    :side-offset="10"
                                                >
                                                    <p>{{ $t("copy") }}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </button>
                                    <a
                                        :href="page.public_url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="bg-white p-2 text-sm font-medium ring-iron-300 hover:bg-iron-50 relative -ml-px inline-flex max-h-[36px] items-center ring-1 ring-inset focus:z-10"
                                        :class="{
                                            'rounded-r-md':
                                                !page.capabilities
                                                    .manage_domains,
                                        }"
                                    >
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <span
                                                        class="inline-flex items-center"
                                                    >
                                                        <ArrowUpRight
                                                            class="text-iron-500"
                                                            :size="20"
                                                        />
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    :side-offset="10"
                                                >
                                                    <p>
                                                        {{
                                                            $t(
                                                                "my_page.editor.open_live_page",
                                                            )
                                                        }}
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </a>
                                    <Link
                                        v-if="page.capabilities.manage_domains"
                                        :href="
                                            route('app.domains.index', {
                                                tab: 'page',
                                                team: page.team.id,
                                            })
                                        "
                                        class="rounded-r-md bg-white p-2 text-sm font-medium ring-iron-300 hover:bg-iron-50 relative -ml-px inline-flex max-h-[36px] items-center ring-1 ring-inset focus:z-10"
                                    >
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <span
                                                        class="inline-flex items-center"
                                                    >
                                                        <Globe2
                                                            class="text-iron-500"
                                                            :size="20"
                                                        />
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    :side-offset="10"
                                                >
                                                    <p>
                                                        {{
                                                            $t(
                                                                "my_page.editor.manage_domain_for",
                                                                {
                                                                    team: page
                                                                        .team
                                                                        .name,
                                                                },
                                                            )
                                                        }}
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </Link>
                                </div>
                                <Link
                                    :href="
                                        route('app.event-type-groups.edit', {
                                            eventTypeGroup: page.id,
                                        })
                                    "
                                >
                                    <Button class="gap-2">
                                        <Settings2 :size="16" />
                                        {{
                                            page.capabilities.edit
                                                ? $t("my_page.overview.edit")
                                                : $t("my_page.overview.view")
                                        }}
                                    </Button>
                                </Link>
                            </div>
                        </article>
                    </div>
                </section>
            </div>
        </div>
    </AppLayout>
</template>
